import { Theme, styled, } from '@mui/material/styles';
import RadioGroup, { useRadioGroup } from '@mui/material/RadioGroup';
import FormControlLabel, { FormControlLabelProps } from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import { Box, FormControl, FormHelperText, FormLabel, Grid, Typography, useTheme } from '@mui/material';
import { useField } from 'formik';
import { ChangeEvent } from 'react';
import { SxProps } from '@mui/system';

interface StyledFormControlLabelProps extends FormControlLabelProps {
  checked: boolean;
}

const StyledFormControlLabel = styled((props: StyledFormControlLabelProps) => (
  <FormControlLabel {...props} />
))(({ theme, checked }) => ({
  padding: "5px",
  borderRadius: "10px",
  backgroundColor: checked
    ? `${theme.palette.primary[500]}1A`
    : `${theme.palette.common.white}0D`,
  border: checked
    ? `1px solid ${theme.palette.primary[500]}`
    : `0.5px solid ${theme.palette.text.secondary}80`,
  color: theme.palette.text.secondary,
  marginLeft: 0,
  marginRight: 0,
  width: "100%",
  columnGap: "5px",
  // Focus styles for keyboard navigation
  '&:has(.Mui-focusVisible)': {
    outline: `2px solid ${theme.palette.primary[500]}`,
    outlineOffset: '2px',
  },
  '.MuiFormControlLabel-label': {
    ...(theme.typography.body_500),
    color: checked ? theme.palette.primary[500] : theme.palette.text.secondary,
  },
}));

function MyFormControlLabel(props: FormControlLabelProps) {
  const radioGroup = useRadioGroup();

  let checked = false;

  if (radioGroup) {
    checked = radioGroup.value === props.value;
  }

  return <StyledFormControlLabel checked={checked} {...props} />;
}

export default function UseRadioGroup({
  name,
  formLabel,
  defaultValue,
  items,
  labelColor = 'rgba(38, 48, 60, 0.7)',
  value = '',
  disabled = false,
  onChange,
  sx = {}
}: {
  name: string;
  formLabel?: string;
  defaultValue?: string | boolean;
  labelColor?: string;
  items: { label: string; value: string | boolean | number; disabled?: boolean }[];
  value?: boolean | string | undefined | number;
  disabled?: boolean;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  sx?: SxProps<Theme>
}): JSX.Element {
  const theme = useTheme()
  const [field, meta] = useField(name);
  const errorText = meta.error && meta.touched ? meta.error : '';
  const errorId = `${name}-radio-error`;

  const fieldValue = value ? value : field.value;
  onChange = onChange || field.onChange;

  return (
    <FormControl component="fieldset" sx={{ width: '100%' }} error={!!errorText}>
      {formLabel && (
        <FormLabel
          component="legend"
          sx={{ mb: 2, fontWeight: 500, fontSize: 14, color: labelColor }}
        >
          {formLabel.split('*')[0]}
          {formLabel.includes('*') && (
            <Box component="span" sx={{ color: theme.palette.error[500] }}>
              *
            </Box>
          )}
        </FormLabel>
      )}
      <RadioGroup
        row
        defaultValue={defaultValue}
        {...field}
        onChange={onChange}
        value={fieldValue}
        aria-label={formLabel || name}
        aria-invalid={!!errorText}
        aria-describedby={errorText ? errorId : undefined}
        aria-required={formLabel?.includes('*') ? true : undefined}
        sx={{
          flexWrap: "nowrap",
          [theme.breakpoints.down('lg')]: {
            flexWrap: "wrap",
            rowGap: "15px"
          },
          columnGap: "15px",
          '& .Mui-disabled': {
            cursor: 'not-allowed !important',
            pointerEvents: 'all',
          },
          ...sx
        }}
      >
        {items.map((item, index) => (
          <MyFormControlLabel
            key={index}
            label={item.label}
            value={item.value}
            disabled={item?.disabled || disabled}
            control={
              <Radio
                size="small"
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    const syntheticEvent = {
                      target: { value: String(item.value), name },
                    } as React.ChangeEvent<HTMLInputElement>;
                    onChange && onChange(syntheticEvent);
                  }
                }}
                sx={{
                  '&.Mui-focusVisible': {
                    outline: '2px solid',
                    outlineColor: 'primary.500',
                    outlineOffset: '2px',
                    borderRadius: '50%',
                  },
                }}
              />
            }
          />
        ))
        }
      </RadioGroup>
      {
        errorText && (
          <FormHelperText error sx={{ marginLeft: 'unset' }} id={errorId} role="alert" aria-live="polite">
            {errorText}
          </FormHelperText>
        )
      }
    </FormControl >
  );
}

const StyledFormControlLabelr = styled((props: StyledFormControlLabelProps) => (
  <FormControlLabel {...props} />
))(({ theme, checked }) => ({
  paddingRight: '10px',
  borderRadius: '12px',
  marginLeft: 0,
  marginBottom: '10px',
  '&:has(.Mui-focusVisible)': {
    outline: `2px solid ${theme.palette.primary[500]}`,
    outlineOffset: '2px',
  },
  '.MuiFormControlLabel-label': {
    fontWeight: 500,
    fontSize: '16px',
    '&': checked && {
      color: theme.palette.primary.main,
    },
  },
}));

export function MyFormControlLabels(props: FormControlLabelProps): JSX.Element {
  const radioGroup = useRadioGroup();

  let checked = false;

  if (radioGroup) {
    checked = radioGroup.value === props.value;
  }

  return <StyledFormControlLabelr checked={checked} {...props} />;
}

export function UseRadioGroupTransparent({
  name,
  defaultValue,
  items,
  value = '',
  disabled = false,
  formLabel,
  onChange,
  sx,
  TableRender,
  relatedPartyLimit,
  row = true,
}: {
  name: string;
  defaultValue?: string | boolean;
  items: { label: any; value: string | boolean | number }[];
  value?: boolean | string | undefined | number;
  disabled?: boolean;
  formLabel?: string;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  sx?: SxProps;
  TableRender?: any;
  relatedPartyLimit?: string;
  row?: boolean;
}): JSX.Element {
  const theme = useTheme();
  const [field, meta] = useField(name);
  const errorText = meta.error && meta.touched ? meta.error : '';

  const fieldValue = value ? value : field.value;
  onChange = onChange || field.onChange;

  return (
    <FormControl component="fieldset" sx={{ width: '100%', ...sx }} error={!!errorText}>
      {formLabel && (
        <FormLabel component="legend" sx={{ mb: 2, fontWeight: 500, fontSize: 14 }}>
          <Typography sx={{ fontWeight: 500, fontSize: 16, color: `${theme.palette.text.primary}`, fontFamily: `${theme.typography.fontFamily}` }}>
            {formLabel}
          </Typography>
        </FormLabel>
      )}
      <RadioGroup
        row={row}
        defaultValue={defaultValue}
        {...field}
        onChange={onChange}
        value={fieldValue}
        aria-label={formLabel || name}
        aria-invalid={!!errorText}
        sx={{
          '& .Mui-disabled': {
            cursor: !disabled ? 'pointer' : 'not-allowed !important',
            pointerEvents: 'all',
          },
        }}>
        {items.map((item, index) => (
          <Box key={index} sx={{ display: 'flex', flexDirection: 'column' }}>
            <MyFormControlLabels
              label={item.label}
              value={item.value}
              disabled={disabled}
              control={
                <Radio
                  size="small"
                  onKeyDown={(e: React.KeyboardEvent) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const syntheticEvent = {
                        target: { value: String(item.value), name },
                      } as React.ChangeEvent<HTMLInputElement>;
                      onChange && onChange(syntheticEvent);
                    }
                  }}
                  sx={{
                    color: 'primary.500',
                    '&.Mui-focusVisible': {
                      outline: '2px solid',
                      outlineColor: 'primary.500',
                      outlineOffset: '2px',
                      borderRadius: '50%',
                    },
                  }}
                />
              }
            />
            {index === 0 && relatedPartyLimit === 'consent' && (
              <Box sx={{ mt: 4, mb: { xs: 4, sm: 0 } }}>{TableRender} </Box>
            )}
          </Box>
        ))}
      </RadioGroup>
      {errorText && (
        <FormHelperText error sx={{ marginLeft: 'unset' }} role="alert" aria-live="polite">
          {errorText}
        </FormHelperText>
      )}
    </FormControl>
  );
}
